import { Dispatch } from 'redux';

import { fetchMarkets } from 'api/apiExchange';
import { EXCHANGE_DECIMALS } from 'config/constants';
import { BigNumberJs } from 'lib/BigNumber';
import {
  Chains,
  chainsMap,
} from 'reduxState/exchange/exchangeActiveChainUtils';
import { ReduxActions, useSelector } from 'reduxState/storeCore';

import { loadMarkets } from './listedMarketsActions';

// Chart search can initialize fetch multiple times, make sure to call just once
let fetching = false;

export const fetchAndDispatchMarkets = (
  dispatch: Dispatch<ReduxActions>,
  loggedIn: boolean
) => {
  if (fetching) {
    console.log('Another Markets fetch in progress - ignoring');
    return;
  }
  fetching = true;
  return fetchMarkets()
    .then(markets => {
      const defaultMarket = markets.find(market => market.default);
      dispatch(
        loadMarkets(
          loggedIn,
          markets,
          defaultMarket
            ? `${defaultMarket?.baseAsset}-${defaultMarket?.quoteAsset}`
            : chainsMap['katanaEVMChain'].fallbackDefaultMarket
        )
      );
    })
    .catch(error => {
      console.warn(error);
      console.error('Cannot fetch assets list');
    })
    .finally(() => {
      fetching = false;
    });
};

/** Get dashboard URL to current or default market or UNDEFINED when not yet initialized */
export const getDashboardUrl = (
  activeChain: Chains,
  market: undefined | string,
  defaultMarket?: undefined | null | string,
  /** Non-reload market switch */
  smoothSwitch = false,
  /** Keep search params. Only need for Applying referral to keep params in address. */
  searchParams?: string
): string => {
  const chain = chainsMap[activeChain];
  if (!chain) {
    console.warn('No Chain provided');
  }
  if (activeChain !== 'katanaEVMChain') {
    console.warn('Unsupported dashboard link on chain');
  }
  const searchOrEmpty = searchParams || '';
  const switchHash = smoothSwitch ? '#switch' : '';
  if (market) {
    return `/trade/${market}${searchOrEmpty}${switchHash}`;
  }
  if (defaultMarket) {
    return `/trade/${defaultMarket}${searchOrEmpty}${switchHash}`;
  }
  // If API default market is initialized (non-undefined),
  // we need to fallback to the static one
  if (defaultMarket === '' || defaultMarket === null) {
    return `/trade/${chain.fallbackDefaultMarket}${searchOrEmpty}${switchHash}`;
  }
  return `/${searchOrEmpty}`;
};

export const calculateTickOrStepSize = (
  tickSizeAsDecimal: string
): {
  inPip: number;
  asDecimal: string;
  visibleDecimals: number;
} => {
  const tickSizeBn = BigNumberJs(tickSizeAsDecimal);
  const tickSizePip = tickSizeBn
    .multipliedBy(10 ** EXCHANGE_DECIMALS)
    .toNumber();
  return {
    inPip: tickSizePip,
    // We will remove any ending 0s
    asDecimal: tickSizeBn.toString(),
    visibleDecimals: Math.max(
      EXCHANGE_DECIMALS - tickSizePip.toString().length + 1,
      0
    ),
  };
};

// export const DEFAULT_TICK_SIZE = calculateTickOrStepSize('0.00000001');

/**
 * Tick size = Minimum price increment change in quote terms
 * https://www.binance.com/en/support/announcement/6925d618ab6b47e2936cc4614eaad64b
 */
export const useSelectMarketTickSize = (
  market: string | undefined
): undefined | ReturnType<typeof calculateTickOrStepSize> => {
  const tickSizes = useSelector(state => state.markets.listedMarkets.tickSizes);
  if (!tickSizes || !market) {
    return undefined;
  }
  return tickSizes[market];
};

/** Minimum quantity increment change in base terms */
export const useSelectMarketStepSize = (
  market: string | undefined
): undefined | ReturnType<typeof calculateTickOrStepSize> => {
  const stepSizes = useSelector(state => state.markets.listedMarkets.stepSizes);
  if (!stepSizes || !market) {
    return undefined;
  }
  return stepSizes[market];
};
